import * as React from "react"
import { defineMessages, FormattedMessage, WrappedComponentProps, useIntl } from "react-intl"
import AnimatedStatusSign from "../AnimatedStatusSign"
import { Status } from "../AnimatedStatusSign/Status"
import { CustomErrorSignificance } from "./Types/CustomErrorSignificance"
import CustomError from "./CustomError"
import * as style from "./style.scss"

const messages = defineMessages({
    generalErrorMessage: { id: "error.generalErrorMessage", defaultMessage: "Undefined error occurred" },
})

interface IErrorProps {
    /** CustomError object which is required to display error message and show error significance */
    error?: CustomError
    /**
     * Callback function triggered when user clicks on retry button.
     * The latter is displayed only if callback is given.
     */
    retryAction?: (event: React.MouseEvent) => void
    /**
     * Callback function triggered when user clicks on reset button.
     * The latter is displayed only if callback is given.
     */
    reset?: () => void
}

/**
 * Component which shows animated error message and optionally retry and reset buttons
 */
const ErrorMessage: React.FunctionComponent<IErrorProps> = ({
    error,
    retryAction,
    reset,
}) => {
    const message = error.getMessage()
    const debugMessage = error.getDebugMessage()
    const intl = useIntl()

    if (!error) {
        error = new CustomError(intl.formatMessage(messages.generalErrorMessage))
    }

    return (
        <div className={style.error}>
            <div>
                {error.errorSignificance === CustomErrorSignificance.NonFatal &&
                    <AnimatedStatusSign status={Status.Warning} />
                }
                {error.errorSignificance === CustomErrorSignificance.Fatal &&
                    <AnimatedStatusSign status={Status.Error} />
                }
                <p className={style.errorMessage}>
                    {message ? (
                        message
                    ) : (
                            intl.formatMessage(messages.generalErrorMessage)
                        )}
                </p>
                {undefined !== debugMessage &&
                    <div className={style.debugMessage}>
                        {debugMessage}
                    </div>
                }
                {reset !== undefined &&
                    <>
                        <p className={style.description}>
                            <FormattedMessage defaultMessage="Note" id="error.note" />
                            <FormattedMessage
                                defaultMessage={`By clicking "{reset}" the application settings will be deleted.`}
                                id="error.resetDescription"
                                values={{
                                    reset: <FormattedMessage defaultMessage="Reset" id="error.reset" />,
                                }}
                            />
                        </p>
                    </>
                }
            </div>
            <div className={style.buttons}>
                {retryAction !== undefined &&
                    <button className={style.button} onClick={retryAction}>
                        <FormattedMessage
                            defaultMessage="Retry"
                            id="error.retry"
                        />
                    </button>
                }
                {reset !== undefined &&
                    <button className={style.button} onClick={reset}>
                        <FormattedMessage id="error.reset" defaultMessage="Reset" />
                    </button>
                }
            </div>
        </div>
    )
}

export default React.memo(ErrorMessage)
